import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { Button } from "components/button";

type Props = {
  onClose: () => void;
};

type Account = {
  id: number;
  name: string;
};

type Division = {
  id: number;
  label: string;
};

type Override = {
  id: number;
  division_id: number;
  division_name: string;
  deposit_account_name: string | null;
  payment_account_name: string | null;
};

const AccountMappingModal: React.FC<Props> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [selectedDivision, setSelectedDivision] = useState("");
  const [depositAccountId, setDepositAccountId] = useState("");
  const [paymentAccountId, setPaymentAccountId] = useState("");

  const { data: divisions = [] } = useQuery("qbo-divisions", async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/local-divisions`);
    return res.data;
  });

  const { data: accounts = [] } = useQuery("qbo-accounts", async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/local-accounts?depositOnly=true`);
    return res.data;
  });

  const { data: overrides = [] } = useQuery("qbo-division-overrides", async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/division-account-overrides`);
    return res.data;
  });

  const handleSaveOverride = async () => {
    await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/map-division-account`, {
      divisionId: selectedDivision,
      depositAccountId: depositAccountId || null,
      paymentAccountId: paymentAccountId || null,
    });
    await queryClient.invalidateQueries("qbo-division-overrides");
    setDepositAccountId("");
    setPaymentAccountId("");
    setSelectedDivision("");
  };

  const handleDeleteOverride = async (overrideId: number) => {
    await axios.delete(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/division-account-override/${overrideId}`);
    await queryClient.invalidateQueries("qbo-division-overrides");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[600px] max-h-[80vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Division Account Mapping</h2>

        <div className="space-y-4">
          <div>
            <label className="block font-medium mb-1">Division</label>
            <select
              className="border rounded px-2 py-1 w-full"
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
            >
              <option value="">-- Select Division --</option>
              {divisions.map((division: Division) => (
                <option key={division.id} value={division.id}>
                  {division.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-medium mb-1">Deposit Account (optional)</label>
            <select
              className="border rounded px-2 py-1 w-full"
              value={depositAccountId}
              onChange={(e) => setDepositAccountId(e.target.value)}
            >
              <option value="">-- None --</option>
              {accounts.map((acct: Account) => (
                <option key={acct.id} value={acct.id}>
                  {acct.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-medium mb-1">Payment Account (optional)</label>
            <select
              className="border rounded px-2 py-1 w-full"
              value={paymentAccountId}
              onChange={(e) => setPaymentAccountId(e.target.value)}
            >
              <option value="">-- None --</option>
              {accounts.map((acct: Account) => (
                <option key={acct.id} value={acct.id}>
                  {acct.name}
                </option>
              ))}
            </select>
          </div>

          <Button onClick={handleSaveOverride} className="w-full">
            Save Override
          </Button>

          <hr className="my-4" />

          <h3 className="text-lg font-semibold">Current Overrides</h3>
          <div className="max-h-[30vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2">Division</th>
                  <th className="text-left py-2">Deposit Account</th>
                  <th className="text-left py-2">Payment Account</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {overrides.map((override: Override) => (
                  <tr key={override.id}>
                    <td className="py-2">{override.division_name}</td>
                    <td className="py-2">{override.deposit_account_name || "--"}</td>
                    <td className="py-2">{override.payment_account_name || "--"}</td>
                    <td className="py-2 text-center">
                      <Button onClick={() => handleDeleteOverride(override.id)} >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-6">
            <Button
              className="ml-2"
              onClick={async () => {
                await axios.get(
                  `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/remote-accounts`
                );
                await queryClient.invalidateQueries("qbo-accounts");
              }}
            >
              Get Accounts
            </Button>
            <Button className="ml-2" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountMappingModal;
